import React, { lazy, useEffect } from "react";

// Libs
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { QueryParamProvider, StringParam, useQueryParams } from "use-query-params";
import Intercom from "@intercom/messenger-js-sdk";

// Midlewares
import RequireAuth from "routes/requirements/RequireAuth";
import RequireShop from "routes/requirements/RequireShop";
import RequireNonAmazonAccount from "routes/requirements/RequireNonAmazonAccount";
import RequireNotBlockedRouteChange from "routes/requirements/RequireNotBlockedRouteChange";

// Routes
import routes from "./routes";
import redirects from "./redirects";

// Utils
import { isSpocketUser } from "utils/email";
import { getIntercomToken } from "utils/api/user/requests";
import paths from "routes/paths";

// Components
import { NotFoundPage } from "pages/NotFound";
import ErrorPage from "pages/ErrorPage";
const loadNavigation = () => import("components/Navigation");
// Every logged-in page is lazy and renders inside the (also lazy) Navigation,
// so its chunk was only requested after Navigation's had loaded — two chunk
// round trips in series before the first page could paint. Starting the
// Navigation download at boot for signed-in users lets the two overlap.
// import() is cached, so lazy() below reuses this request.
if (localStorage.getItem("user_id") && localStorage.getItem("dropshipper_id")) {
  // A failed early fetch is retried by lazy() below; don't report it twice.
  loadNavigation().catch(() => {});
}
const Navigation = lazy(loadNavigation);

let intercomBootedForUserId = null;

const AUTH_PATHS = Object.values(paths.auth);

const bootIntercomForUser = async (userId, email) => {
  const appId = process.env.REACT_APP_INTERCOM_KEY;
  if (!appId) return;
  if (intercomBootedForUserId === userId) return;
  intercomBootedForUserId = userId;

  let intercom_user_jwt;
  try {
    ({ intercom_user_jwt } = await getIntercomToken());
  } catch (error) {
    intercomBootedForUserId = null;
    console.error("Failed to fetch Intercom token", error);
    return;
  }

  Intercom({
    app_id: appId,
    user_id: `spocket_${userId}`,
    email,
    intercom_user_jwt
  });
};

const navWrapper = Component => props => {
  const [query] = useQueryParams({
    pricing: StringParam,
    show_rating: StringParam
  });

  return (
    <Navigation {...props} pricing={query.pricing} showRating={query.show_rating}>
      <Component />
    </Navigation>
  );
};

const WrapperUseQueryParams = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (AUTH_PATHS.includes(location.pathname)) return;
    const userId = localStorage.getItem("user_id");
    const dropshipperId = localStorage.getItem("dropshipper_id");
    const email = localStorage.getItem("user_email");
    if (!userId || !dropshipperId) return;
    bootIntercomForUser(dropshipperId, email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryParamProvider
      history={{ replace: navigate, push: navigate }}
      location={location}
      adapter={ReactRouter6Adapter}
    >
      {children}
    </QueryParamProvider>
  );
};

export const makeRedirects = () => {
  return redirects.map(({ from, to }) => ({
    path: from,
    element: <Navigate to={to} replace />
  }));
};

export const makeRoutes = () => {
  const isSpocketUserVal = isSpocketUser();

  return [
    ...routes
      .map(({ path, Component, app, shop, blockedForAmazonUsers, spocketUser }) => {
        if (app) {
          if (isSpocketUserVal) {
            Component = RequireAuth(navWrapper(Component));
          } else {
            // Sometimes users might be blocked on Search page so we should not allow them to go to other routes
            Component = RequireAuth(RequireNotBlockedRouteChange(navWrapper(Component)));
          }
        }

        if (shop) {
          Component = RequireShop(Component);
        }

        if (blockedForAmazonUsers) {
          Component = RequireNonAmazonAccount(Component);
        }

        // If route is set to be used by Spocket user and user is not Spocket, return null
        if (spocketUser !== undefined && spocketUser !== isSpocketUserVal) return null;

        return {
          path,
          element: (
            <WrapperUseQueryParams>
              <Component />
            </WrapperUseQueryParams>
          ),
          errorElement: <ErrorPage />
        };
      })
      .filter(t => t),
    { errorElement: <NotFoundPage /> }
  ];
};
